import React, { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Plus, Search, Drum, Piano, Waves, AudioLines, Music2, Sparkle, Play, Repeat, FileUp } from "lucide-react";
import { INSTRUMENTS, engine } from "./engine";
import { LOOPS, PATTERNS, TRACK_COLORS } from "./songData";

const ICON = { drums: Drum, keys: Piano, pad: Waves, bass: AudioLines, lead: Music2, pluck: Sparkle };
const FX = ["Pro EQ³", "Compressor", "Room Reverb", "Analog Delay", "Limiter²", "Chorus", "Tape Saturation", "Transient Shaper", "Auto Filter", "Pitch Correct"];

export function Inspector({ song, mutate, sel }) {
  const ti = song.tracks.findIndex((t) => t.id === sel.trackId);
  const t = song.tracks[ti];
  if (!t) return <div className="p-4 text-xs text-zinc-500">Select a track to inspect.</div>;
  const set = (k, v) => mutate((d) => { d.tracks[ti][k] = v; });
  return (
    <div className="p-3 space-y-4 text-xs" data-testid="inspector">
      <div>
        <div className="text-[10px] font-mono text-zinc-500 mb-1">TRACK NAME</div>
        <Input value={t.name} onChange={(e) => set("name", e.target.value)} className="h-8 text-xs bg-black/30 border-black" data-testid="inspector-name" />
      </div>
      <div>
        <div className="text-[10px] font-mono text-zinc-500 mb-1">INSTRUMENT</div>
        <Select value={t.instrument} onValueChange={(v) => set("instrument", v)}>
          <SelectTrigger className="h-8 text-xs bg-black/30 border-black" data-testid="inspector-instrument"><SelectValue /></SelectTrigger>
          <SelectContent>{INSTRUMENTS.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <div className="text-[10px] font-mono text-zinc-500 mb-1.5">COLOR</div>
        <div className="flex flex-wrap gap-1.5">{TRACK_COLORS.map((c) => <button key={c} onClick={() => set("color", c)} className={`w-5 h-5 rounded ${t.color === c ? "ring-2 ring-white" : ""}`} style={{ background: c }} />)}</div>
      </div>
      {[["volume", "VOLUME", 0, 1], ["cutoff", "FILTER CUTOFF", 0, 1], ["sendRev", "REVERB SEND", 0, 1], ["sendDly", "DELAY SEND", 0, 1]].map(([k, l, a, b]) => (
        <div key={k}>
          <div className="flex justify-between text-[10px] font-mono text-zinc-500 mb-1.5"><span>{l}</span><span className="text-zinc-300">{Math.round((t[k] ?? 0) * 100)}%</span></div>
          <Slider min={a} max={b} step={0.01} value={[t[k] ?? 0]} onValueChange={([v]) => set(k, v)} />
        </div>
      ))}
      {t.instrument !== "drums" && (
        <div>
          <div className="flex justify-between text-[10px] font-mono text-zinc-500 mb-1.5"><span>TRANSPOSE</span><span className="text-zinc-300">{t.transpose || 0} st</span></div>
          <Slider min={-12} max={12} step={1} value={[t.transpose || 0]} onValueChange={([v]) => set("transpose", v)} />
        </div>
      )}
      <div className="rounded bg-black/30 p-2 space-y-1">
        <div className="text-[10px] font-mono text-zinc-500">INSERTS</div>
        <div className="flex items-center justify-between bg-[#1d1f23] rounded px-2 py-1"><span>Pro EQ³</span><span className={`w-2 h-2 rounded-full ${t.eqOn !== false ? "bg-sky-400" : "bg-zinc-600"}`} /></div>
        <div className="text-[10px] text-zinc-600">{t.clips.length} clips · {t.clips.reduce((a, c) => a + c.notes.length, 0)} notes</div>
      </div>
    </div>
  );
}

export default function Browser({ song, mutate, setSel, locator }) {
  const [q, setQ] = useState("");
  const addTrack = (inst, loop) => {
    const id = "t_" + Math.random().toString(36).slice(2, 8);
    const I = INSTRUMENTS.find((i) => i.id === inst);
    const clipId = "c_" + Math.random().toString(36).slice(2, 8);
    const start = loop ? Math.min(song.bars - loop.bars, Math.floor(locator / 16)) : 0;
    mutate((d) => {
      d.tracks.push({ id, name: loop ? loop.name : I.name.split(" ")[0], instrument: inst, color: TRACK_COLORS[d.tracks.length % TRACK_COLORS.length], volume: 0.75, pan: 0, mute: false, solo: false, arm: false, eq: { low: 0, mid: 0, high: 0 }, eqOn: true, sendRev: 0.1, sendDly: 0, cutoff: 0.7, transpose: 0,
        clips: loop ? [{ id: clipId, name: loop.name, start, length: loop.bars, notes: PATTERNS[loop.id](loop.bars).map((n) => ({ ...n })) }] : [] });
    });
    setSel({ trackId: id, clipId: loop ? clipId : null });
  };
  const audition = (loop) => {
    engine.init();
    const tmp = { id: "__preview", instrument: loop.inst, volume: 0.7, pan: 0, eq: { low: 0, mid: 0, high: 0 }, cutoff: 0.7 };
    const ch = engine.channel(tmp); ch.fader.gain.value = 0.5;
    const sd = 60 / song.bpm / 4, t0 = engine.ctx.currentTime + 0.05;
    PATTERNS[loop.id](1).forEach((n) => engine.note(tmp, n.p, t0 + n.s * sd, n.l * sd, n.v));
  };
  const f = (s) => s.toLowerCase().includes(q.toLowerCase());
  return (
    <div className="flex flex-col h-full" data-testid="browser-panel">
      <div className="p-2 border-b border-black space-y-2">
        <button onClick={() => document.querySelector('[data-testid="import-audio-input"]')?.click()} className="w-full h-8 rounded bg-sky-400/10 border border-sky-400/30 text-sky-300 text-xs flex items-center justify-center gap-1.5 hover:bg-sky-400/20" data-testid="browser-import-audio"><FileUp className="w-3.5 h-3.5" />Import audio file (mp3, wav…)</button>
        <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-zinc-500" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search browser" className="h-8 pl-7 text-xs bg-black/30 border-black" data-testid="browser-search" /></div>
      </div>
      <Tabs defaultValue="inst" className="flex-1 flex flex-col min-h-0">
        <TabsList className="mx-2 mt-2 bg-black/30 h-8 grid grid-cols-3">
          <TabsTrigger value="inst" className="text-[11px]" data-testid="browser-tab-instruments">Instruments</TabsTrigger>
          <TabsTrigger value="loops" className="text-[11px]" data-testid="browser-tab-loops">Loops</TabsTrigger>
          <TabsTrigger value="fx" className="text-[11px]">Effects</TabsTrigger>
        </TabsList>
        <TabsContent value="inst" className="flex-1 overflow-auto thin-scroll p-2 space-y-1 mt-1">
          {INSTRUMENTS.filter((i) => f(i.name)).map((i) => { const I = ICON[i.id]; return (
            <div key={i.id} className="group flex items-center gap-2 p-2 rounded hover:bg-white/5">
              <div className="w-8 h-8 rounded bg-black/40 flex items-center justify-center"><I className="w-4 h-4 text-amber-400" /></div>
              <div className="flex-1 min-w-0"><div className="text-xs truncate">{i.name}</div><div className="text-[10px] text-zinc-500">{i.desc}</div></div>
              <button onClick={() => { engine.preview({ id: "__preview", instrument: i.id, volume: 0.7, pan: 0, cutoff: 0.7 }, i.id === "drums" ? 36 : 60, 0.5); }} className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-white" title="Preview"><Play className="w-3.5 h-3.5" /></button>
              <button onClick={() => addTrack(i.id)} data-testid={`add-inst-${i.id}`} className="w-6 h-6 rounded bg-amber-400/10 text-amber-400 hover:bg-amber-400 hover:text-black flex items-center justify-center" title="Add track"><Plus className="w-3.5 h-3.5" /></button>
            </div>); })}
        </TabsContent>
        <TabsContent value="loops" className="flex-1 overflow-auto thin-scroll p-2 mt-1">
          {["Drums", "Bass", "Keys", "Synth"].map((cat) => (
            <div key={cat} className="mb-3">
              <div className="text-[10px] font-mono text-zinc-500 px-2 mb-1">{cat.toUpperCase()}</div>
              {LOOPS.filter((l) => l.cat === cat && f(l.name)).map((l) => (
                <div key={l.id} className="group flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5">
                  <Repeat className="w-3.5 h-3.5 text-sky-400" />
                  <div className="flex-1 text-xs truncate">{l.name}</div>
                  <span className="text-[10px] font-mono text-zinc-600">{l.bars} bars</span>
                  <button onClick={() => audition(l)} className="text-zinc-400 hover:text-white" title="Audition 1 bar" data-testid={`audition-${l.id}`}><Play className="w-3.5 h-3.5" /></button>
                  <button onClick={() => addTrack(l.inst, l)} data-testid={`add-loop-${l.id}`} className="w-6 h-6 rounded bg-sky-400/10 text-sky-400 hover:bg-sky-400 hover:text-black flex items-center justify-center" title="Add at playhead"><Plus className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          ))}
        </TabsContent>
        <TabsContent value="fx" className="flex-1 overflow-auto thin-scroll p-2 mt-1 space-y-1">
          {FX.filter(f).map((x) => <div key={x} className="text-xs px-2 py-2 rounded hover:bg-white/5 flex items-center gap-2"><AudioLines className="w-3.5 h-3.5 text-emerald-400" />{x}</div>)}
          <p className="text-[10px] text-zinc-600 px-2 pt-2">EQ, reverb & delay are live on every channel via the mixer.</p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
